/**
 * Cross-tab authentication synchronization using Broadcast Channel API
 *
 * Since sessionStorage is isolated per tab, we use the Broadcast Channel API
 * to synchronize authentication state across tabs while keeping tokens
 * in sessionStorage for security.
 *
 * Benefits over localStorage events:
 * - More efficient (no polling)
 * - Better performance
 * - Designed specifically for cross-tab communication
 * - Doesn't pollute storage
 * - Automatic cleanup
 */

const AUTH_CHANNEL_NAME = 'breeze_auth_channel';

interface AuthSyncMessage {
  type: 'AUTH_STATE_CHANGED' | 'AUTH_REQUEST' | 'AUTH_RESPONSE' | 'LOGOUT';
  timestamp: number;
  hasAuth: boolean;
  sessionStorageData?: Record<string, string>;
}

// Create a single broadcast channel instance
let authChannel: BroadcastChannel | null = null;

/**
 * Get or create the auth broadcast channel
 */
function getAuthChannel(): BroadcastChannel {
  if (!authChannel) {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  }
  return authChannel;
}

/**
 * Broadcast authentication state to other tabs
 */
export function broadcastAuthState(hasAuth: boolean) {
  const message: AuthSyncMessage = {
    type: 'AUTH_STATE_CHANGED',
    timestamp: Date.now(),
    hasAuth,
  };

  const channel = getAuthChannel();
  channel.postMessage(message);
}

/**
 * Request auth state from other tabs
 */
export function requestAuthFromOtherTabs(): Promise<boolean> {
  return new Promise((resolve) => {
    const message: AuthSyncMessage = {
      type: 'AUTH_REQUEST',
      timestamp: Date.now(),
      hasAuth: false,
    };

    let responseReceived = false;
    const channel = getAuthChannel();

    // Listen for responses
    const handleMessage = (event: MessageEvent<AuthSyncMessage>) => {
      const data = event.data;

      if (data.type === 'AUTH_RESPONSE' && data.hasAuth && data.sessionStorageData) {
        responseReceived = true;

        // Copy sessionStorage data from the authenticated tab
        Object.entries(data.sessionStorageData).forEach(([key, value]) => {
          sessionStorage.setItem(key, value);
        });

        channel.removeEventListener('message', handleMessage);
        resolve(true);
      }
    };

    channel.addEventListener('message', handleMessage);

    // Broadcast request
    channel.postMessage(message);

    // Timeout after 500ms
    setTimeout(() => {
      channel.removeEventListener('message', handleMessage);
      if (!responseReceived) {
        resolve(false);
      }
    }, 500);
  });
}

/**
 * Broadcast logout to all tabs
 */
export function broadcastLogout() {
  const message: AuthSyncMessage = {
    type: 'LOGOUT',
    timestamp: Date.now(),
    hasAuth: false,
  };

  const channel = getAuthChannel();
  channel.postMessage(message);
}

/**
 * Listen for logout events from other tabs
 */
export function setupLogoutListener(onLogout: () => void) {
  const channel = getAuthChannel();

  const handleMessage = (event: MessageEvent<AuthSyncMessage>) => {
    const data = event.data;

    if (data.type === 'LOGOUT') {
      // Clear sessionStorage and trigger logout
      sessionStorage.clear();
      onLogout();
    }
  };

  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
  };
}

/**
 * Listen for auth requests from other tabs and respond if authenticated
 */
export function setupAuthRequestListener(checkAuthCallback: () => boolean) {
  const channel = getAuthChannel();

  const handleMessage = (event: MessageEvent<AuthSyncMessage>) => {
    const data = event.data;

    if (data.type === 'AUTH_REQUEST') {
      const hasAuth = checkAuthCallback();

      if (hasAuth) {
        // Copy all OIDC-related sessionStorage items
        const sessionStorageData: Record<string, string> = {};
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.startsWith('oidc.') || key.includes('user'))) {
            const value = sessionStorage.getItem(key);
            if (value) {
              sessionStorageData[key] = value;
            }
          }
        }

        const response: AuthSyncMessage = {
          type: 'AUTH_RESPONSE',
          timestamp: Date.now(),
          hasAuth: true,
          sessionStorageData,
        };

        channel.postMessage(response);
      }
    }
  };

  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
  };
}

/**
 * Close the broadcast channel (cleanup)
 */
export function closeAuthChannel() {
  if (authChannel) {
    authChannel.close();
    authChannel = null;
  }
}
